import React, { useRef, useState } from "react";
import { UploadCloud, X, Loader2, GripVertical } from "lucide-react";
import api, { resolveImg } from "../lib/api";
import { toast } from "sonner";

/**
 * Multi-image uploader.
 * Props:
 *   - value: array of image URLs
 *   - onChange: called with new array
 *   - label: optional
 *   - max: max images (default 6)
 */
export default function MultiImageUpload({ value = [], onChange, label = "Additional images", max = 6, testid = "multi-image-upload" }) {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const [dragIndex, setDragIndex] = useState(null);

  const uploadOne = async (file) => {
    if (!file) return null;
    if (!/^image\/(jpeg|png|webp|gif)$/.test(file.type)) {
      toast.error(`"${file.name}" — please upload JPG, PNG, WEBP, or GIF.`);
      return null;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error(`"${file.name}" is too large (${(file.size / 1024 / 1024).toFixed(1)} MB). Max 5 MB.`);
      return null;
    }
    const form = new FormData();
    form.append("file", file);
    try {
      const { data } = await api.post("/admin/upload/image", form, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      return data.url;
    } catch (err) {
      toast.error(err.response?.data?.detail || `Upload of "${file.name}" failed`);
      return null;
    }
  };

  const handleFiles = async (files) => {
    const list = Array.from(files || []);
    if (list.length === 0) return;
    const slots = max - value.length;
    if (slots <= 0) {
      toast.error(`You already have ${max} images — remove one to add more.`);
      return;
    }
    const chosen = list.slice(0, slots);
    setUploading(true);
    // Upload all files, collect URLs, then call onChange once with the full batch.
    // (Calling onChange per-upload causes stale-closure overwrites where only the
    // last upload's array sticks.)
    const newUrls = [];
    for (const file of chosen) {
      const url = await uploadOne(file);
      if (url) newUrls.push(url);
    }
    setUploading(false);
    if (newUrls.length > 0) {
      onChange([...value, ...newUrls]);
    }
    if (list.length > slots) {
      toast.info(`Only ${newUrls.length} uploaded — max is ${max} images.`);
    } else if (newUrls.length === chosen.length) {
      toast.success(`${newUrls.length} image${newUrls.length === 1 ? "" : "s"} uploaded`);
    } else if (newUrls.length > 0) {
      toast.success(`${newUrls.length} of ${chosen.length} uploaded`);
    }
  };

  const remove = (i) => {
    const next = [...value];
    next.splice(i, 1);
    onChange(next);
  };

  const onDropItem = (e, target) => {
    e.preventDefault();
    if (dragIndex === null || dragIndex === target) return;
    const next = [...value];
    const [moved] = next.splice(dragIndex, 1);
    next.splice(target, 0, moved);
    onChange(next);
    setDragIndex(null);
  };

  return (
    <div data-testid={testid}>
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-medium uppercase tracking-wide text-[var(--ink-forest)]">
          {label}
        </span>
        <span className="text-[10px] text-[var(--muted-fg)]">{value.length} / {max}</span>
      </div>

      <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
        {value.map((url, i) => (
          <div
            key={i}
            draggable
            onDragStart={() => setDragIndex(i)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => onDropItem(e, i)}
            className="relative aspect-square rounded-xl overflow-hidden border border-[var(--border)] bg-[var(--muted)] group cursor-move"
            data-testid={`${testid}-item-${i}`}
          >
            <img src={resolveImg(url)} alt="" className="w-full h-full object-cover" />
            <div className="absolute top-1.5 left-1.5 bg-[var(--bg-surface)]/90 w-6 h-6 rounded-full flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
              <GripVertical className="w-3.5 h-3.5 text-[var(--muted-fg)]" />
            </div>
            <button
              type="button"
              onClick={() => remove(i)}
              className="absolute top-1.5 right-1.5 bg-[var(--bg-surface)] text-rose-700 w-6 h-6 rounded-full flex items-center justify-center shadow-md opacity-0 group-hover:opacity-100 transition-opacity"
              aria-label="Remove image"
              data-testid={`${testid}-remove-${i}`}
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}

        {value.length < max && (
          <button
            type="button"
            onClick={() => inputRef.current?.click()}
            disabled={uploading}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => {
              e.preventDefault();
              handleFiles(e.dataTransfer.files);
            }}
            className="aspect-square rounded-xl border-2 border-dashed border-[var(--border)] bg-[var(--bg-cream)] hover:border-[var(--terracotta)] flex flex-col items-center justify-center gap-1.5 transition-colors disabled:opacity-60"
            data-testid={`${testid}-add`}
          >
            {uploading ? (
              <Loader2 className="w-5 h-5 text-[var(--terracotta)] animate-spin" />
            ) : (
              <UploadCloud className="w-5 h-5 text-[var(--terracotta)]" />
            )}
            <span className="text-[10px] text-[var(--muted-fg)] text-center px-2">
              {uploading ? "Uploading…" : "Add"}
            </span>
          </button>
        )}
      </div>

      <input
        ref={inputRef}
        type="file"
        accept="image/jpeg,image/png,image/webp,image/gif"
        multiple
        className="hidden"
        onChange={(e) => handleFiles(e.target.files)}
      />

      {value.length > 1 && (
        <p className="mt-2 text-[10px] text-[var(--muted-fg)]">
          Tip: drag images to reorder. The first image here shows after the main image in the gallery.
        </p>
      )}
    </div>
  );
}
